export const initProfileEditVideo = () => {
  const profileVideo = document.getElementById("profile-video") as HTMLDivElement;

  if (!profileVideo) {
    return;
  }

  /**
   * Example from: https://github.com/webrtc/samples/blob/gh-pages/src/content/getusermedia/record/js/main.js
   */
  if (!hasGetUserMedia()) {
    showBrowserSupport();

    return;
  }

  const PROGRESS_INTERVAL_TIMEOUT_MS = 200;
  const MIN_VIDEO_DURATION_MS = 10000;
  const MAX_VIDEO_DURATION_MS = 40000;
  const MEDIA_CONSTRAINTS = {
    video: {
      width: { max: 1280 },
      height: { max: 720 },
      facingMode: "user",
    },
    audio: true,
  };

  const preview = document.getElementById("preview") as HTMLVideoElement;
  const description = document.getElementById("description") as HTMLDivElement;
  const recording = document.getElementById("recording") as HTMLVideoElement;
  const openCameraButton = document.getElementById("openCameraButton") as HTMLButtonElement;

  const actionButtonWrapper = document.querySelector(".action-button-wrapper") as HTMLDivElement;
  const actionTooltip = actionButtonWrapper.querySelector(".description-tooltip") as HTMLDivElement;
  const actionButton = actionButtonWrapper.querySelector(
    ".action-button-wrapper .action-button",
  ) as HTMLButtonElement;

  const counterWrapper = profileVideo.querySelector(".counter-wrapper") as HTMLDivElement;
  const counterInner = counterWrapper.querySelector(".counter-inner") as HTMLDivElement;

  const acceptDeclineButtons = document.getElementById("acceptDeclineButtons") as HTMLDivElement;
  const tryAgainVideoButton = document.getElementById("tryAgainVideoButton") as HTMLButtonElement;
  const acceptVideoButton = document.getElementById("acceptVideoButton") as HTMLButtonElement;

  const loader = document.getElementById("loader") as HTMLDivElement;
  const uploader = document.getElementById("uploader") as HTMLDivElement;
  const success = profileVideo.querySelector(".success") as HTMLDivElement;

  //Init
  let mediaRecorder: MediaRecorder;
  let videoDurationMs = 0;
  let videoDurationInterval: NodeJS.Timeout | null = null;

  setUp();
  showDescription();

  /**
   * Events Listeners
   */
  openCameraButton.addEventListener("click", openCameraHandler);
  actionButton.addEventListener("click", videoActionHandler);
  tryAgainVideoButton.addEventListener("click", tryAgainHandler);
  acceptVideoButton.addEventListener("click", acceptVideoHandler);

  /**
   * Setup
   */
  function setUp() {
    counterInner.innerText = formatCounterTime(0);
  }

  /**
   * Event Handlers
   */
  function openCameraHandler() {
    openCamera();
  }

  function videoActionHandler() {
    if (videoDurationInterval) {
      stopVideoRecording();

      if (videoDurationMs < MIN_VIDEO_DURATION_MS) {
        tryRecordVideoAgain();
        alert(actionButtonWrapper.dataset.videoLengthError);
      }

      return;
    }

    starVideoRecording();
  }

  function tryAgainHandler() {
    hideAcceptDeclineButtons();

    showPreview();

    stopVideoRecording();
    resetVideoCounter();
    tryRecordVideoAgain();
  }

  function acceptVideoHandler() {
    const userId = profileVideo.dataset.userId;

    const uri = `/website-api/users/${userId}/video/`;
    const xhr = new XMLHttpRequest();
    const fd = new FormData();

    const uploadStatusPercentage = uploader.querySelector(
      ".upload-status-percentage",
    ) as HTMLDivElement;
    const uploadStatusChecking = uploader.querySelector(
      ".upload-status-checking",
    ) as HTMLDivElement;

    uploadStatusPercentage.textContent = "";

    recording.pause();
    recording.style.display = "none";

    hideAcceptDeclineButtons();

    uploader.classList.remove("d-none");
    uploadStatusChecking.classList.add("d-none");
    uploadStatusPercentage.classList.remove("d-none");

    xhr.upload.addEventListener(
      "progress",
      (e) => {
        if (e.lengthComputable) {
          const percentage = Math.round((e.loaded * 100) / e.total);

          uploadStatusPercentage.textContent = `${percentage}%`;

          if (percentage === 100) {
            uploadStatusPercentage.classList.add("d-none");
            uploadStatusChecking.classList.remove("d-none");
          }
        }
      },
      false,
    );

    xhr.onreadystatechange = () => {
      if (xhr.readyState === 4) {
        uploader.classList.add("d-none");
      }

      if (xhr.readyState === 4 && xhr.status === 413) {
        showAcceptDeclineButtons();

        alert(profileVideo.dataset.tooLargeError ?? "");

        return;
      }

      if (xhr.readyState === 4 && xhr.status === 422) {
        showPreview();

        stopVideoRecording();
        resetVideoCounter();
        tryRecordVideoAgain();

        const jsonResponse = JSON.parse(xhr.response);

        alert(jsonResponse.metadata.errors[0].error);

        return;
      }

      if (xhr.readyState === 4 && xhr.status === 409) {
        showAcceptDeclineButtons();

        const jsonResponse = JSON.parse(xhr.response);

        alert(jsonResponse.metadata.error_message);

        return;
      }

      if (xhr.readyState === 4 && xhr.status === 200) {
        hideAcceptDeclineButtons();

        success.classList.remove("d-none");
      }
    };

    xhr.open("POST", uri);
    // @ts-expect-error: Recording contains file
    fd.append("file", recording.file, "recording.webm");
    // Initiate a multipart/form-data upload
    xhr.send(fd);
  }

  function videoDurationCounter() {
    videoDurationMs += PROGRESS_INTERVAL_TIMEOUT_MS;

    // Count down from 40 seconds instead of counting up
    const remainingSeconds = Math.max(
      0,
      MAX_VIDEO_DURATION_MS / 1000 - Math.floor(videoDurationMs / 1000),
    );
    counterInner.innerText = formatCounterTime(remainingSeconds);
  }

  function startVideoCounter() {
    videoDurationMs = 0;

    if (videoDurationInterval) {
      clearInterval(videoDurationInterval);
    }

    // Initialize counter to show 40 seconds (countdown starting point)
    counterInner.innerText = formatCounterTime(MAX_VIDEO_DURATION_MS / 1000);
    videoDurationInterval = setInterval(videoDurationCounter, PROGRESS_INTERVAL_TIMEOUT_MS);
  }

  function resetVideoCounter() {
    videoDurationMs = 0;

    if (videoDurationInterval) {
      clearInterval(videoDurationInterval);
      videoDurationInterval = null;
    }

    counterInner.innerText = formatCounterTime(0);
    hideCounter();
  }

  function toggleButtonColor(inProgress: boolean) {
    if (inProgress) {
      actionButton.classList.remove("btn-secondary");
      actionButton.classList.add("btn-danger");
      actionButton.classList.add("bg-danger-600");
    } else {
      actionButton.classList.remove("btn-danger");
      actionButton.classList.remove("bg-danger-600");
      actionButton.classList.add("btn-secondary");
    }
  }

  /**
   * Check browser support for recording videos
   */
  function hasGetUserMedia(): boolean {
    if (!navigator.mediaDevices?.getUserMedia) {
      return false;
    }

    return true;
  }

  function openCamera() {
    navigator.mediaDevices
      .getUserMedia(MEDIA_CONSTRAINTS)
      .then((stream) => {
        //Show record tn here

        hideDescription();
        showLoader();
        showVideoActionButton();

        preview.srcObject = stream;
        // @ts-expect-error: Capture stream does exist
        preview.captureStream = preview.captureStream || preview.mozCaptureStream;

        /**
         * Here we check the captureStream support. This is currently not supported in iOS 14.4
         */
        // @ts-expect-error: Capture stream does exist
        if (!preview.captureStream) {
          hideLoader();
          showBrowserSupport();

          return;
        }

        return new Promise((resolve) => (preview.onplaying = resolve)).then(() => {
          hideLoader();
          showVideoActionButton();
        });
      })
      .catch((err) => {
        /* handle the error */
        resetVideoCounter();
        alert(err);
      });
  }

  function starVideoRecording() {
    applyActionStopState();
    startVideoCounter();
    toggleButtonColor(true);
    showCounter();

    // @ts-expect-error: Capture stream does exist
    startRecording(preview.captureStream(), MAX_VIDEO_DURATION_MS)
      .then((recordedChunks) => {
        if (videoDurationMs < MIN_VIDEO_DURATION_MS) {
          resetVideoCounter();
          return;
        }

        resetVideoCounter();

        return showRecordedVideo(recordedChunks);
      })
      .catch((err) => {
        /* handle the error */
        resetVideoCounter();
        toggleButtonColor(false);
        hideCounter();
        alert(err);
      });
  }

  function stopVideoRecording() {
    applyActionStartState();
    stopVideoStream(preview.srcObject as MediaStream);
    toggleButtonColor(false);
    hideCounter();
  }

  function tryRecordVideoAgain() {
    recording.style.display = "none";

    recording.pause();

    openCamera();
  }

  function startRecording(stream: MediaStream, stopLengthInMS: number) {
    // var options;
    // if (MediaRecorder.isTypeSupported('video/webm;codecs=vp9')) {
    //     options = {mimeType: 'video/webm; codecs=vp9'};
    // } else if (MediaRecorder.isTypeSupported('video/webm;codecs=vp8')) {
    //     options = {mimeType: 'video/webm; codecs=vp8'};
    // } else {
    //     alert('Browser lacks support.');
    // }

    mediaRecorder = new MediaRecorder(stream);

    const data: Blob[] = [];

    mediaRecorder.ondataavailable = (event) => data.push(event.data);

    try {
      mediaRecorder.start();
    } catch (_e) {
      resetVideoCounter();
      tryRecordVideoAgain();
    }

    let elapsedTime = 0;

    const counter = setInterval(() => {
      if (elapsedTime < stopLengthInMS) {
        elapsedTime = elapsedTime + 100;
      } else {
        stopVideoStream(preview.srcObject as MediaStream);

        clearInterval(counter);
      }
    }, 100);

    const stopped = new Promise((resolve, reject) => {
      mediaRecorder.onstop = resolve;
      // @ts-expect-error: Event name
      mediaRecorder.onerror = (event) => reject(event.name);
    }).then(() => {
      clearInterval(counter);
    });

    return Promise.all([stopped]).then(() => {
      return data;
    });
  }

  function showRecordedVideo(recordedChunks: BlobPart[]) {
    hidePreview();

    hideVideoActionButton();
    showAcceptDeclineButtons();

    const recordedBlob = new Blob(recordedChunks, {
      type: "video/webm",
    });
    recording.src = URL.createObjectURL(recordedBlob);
    // @ts-expect-error: File does exists
    recording.file = recordedBlob;
  }

  function showPreview() {
    preview.style.display = "block";
    recording.style.display = "none";
  }

  function hidePreview() {
    preview.style.display = "none";
    recording.style.display = "block";
  }

  function showAcceptDeclineButtons() {
    acceptDeclineButtons.classList.remove("d-none");
  }

  function hideAcceptDeclineButtons() {
    acceptDeclineButtons.classList.add("d-none");
  }

  function showVideoActionButton() {
    actionButtonWrapper.classList.remove("d-none");
  }

  function hideVideoActionButton() {
    actionButtonWrapper.classList.add("d-none");
  }

  function showDescription() {
    description.classList.remove("d-none");
  }

  function hideDescription() {
    description.classList.add("d-none");
  }

  function showLoader() {
    loader.classList.remove("d-none");
  }

  function hideLoader() {
    loader.classList.add("d-none");
  }

  function showBrowserSupport() {
    const browserSupport = profileVideo.querySelector(".browser-support") as HTMLDivElement;
    browserSupport.classList.remove("d-none");
  }

  function applyActionStopState() {
    actionTooltip.classList.add("d-none");
  }

  function applyActionStartState() {
    actionTooltip.classList.remove("d-none");
  }

  function stopVideoStream(stream: MediaStream) {
    // biome-ignore lint/suspicious/useIterableCallbackReturn: <Refactor later>
    stream.getTracks().forEach((track) => track.stop());
  }

  function showCounter() {
    counterWrapper.classList.remove("d-none");
  }

  function hideCounter() {
    counterWrapper.classList.add("d-none");
  }

  function formatCounterTime(totalSeconds: number) {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return [hours, minutes, seconds].map((unit) => String(unit).padStart(2, "0")).join(":");
  }
};
